import { HTMLToMarkdownConverter } from '@/pages/aigc/pages/Agent/ChatDSTemplate/utils';
import type {
  ICreateKBParams,
  IKnowledgeBaseItem,
} from '@/pages/aigc/pages/Agent/components/QAInput/KnowledgeBaseMention/hooks/useKnowledgeBaseApi';
import type { TKnowledgeAddTab } from './hooks/useKnowledgeBaseAddFormConfig';

export interface IKbDefaultModelIds {
  summary_model_id: string;
  embedding_model_id: string;
}

export interface IBuildCreateKbPayloadOptions {
  kbName: string;
  kbDefaultModelIds: IKbDefaultModelIds;
  description?: string;
  aiEnabled?: boolean;
  title?: string;
  content?: string;
}

/** 新建知识库默认配置（与「新建知识库」Tab 一致） */
export const buildDefaultCreateKbPayload = (
  options: IBuildCreateKbPayloadOptions,
): ICreateKBParams => {
  const aiEnabled = options.aiEnabled ?? false;
  const payload: ICreateKBParams = {
    name: options.kbName,
    type: 'document',
    summary_model_id: options.kbDefaultModelIds.summary_model_id,
    embedding_model_id: options.kbDefaultModelIds.embedding_model_id,
    chunking_config: {
      chunk_size: aiEnabled ? 2048 : 512,
      chunk_overlap: aiEnabled ? 0 : 80,
      separators: ['\n\n', '\n', '。', '！', '？', ';', '；'],
      enable_multimodal: false,
      enable_parent_child: !aiEnabled,
      parent_chunk_size: 4096,
      child_chunk_size: 384,
      strategy: 'auto',
      token_limit: 0,
      languages: [],
    },
    vlm_config: { enabled: false, model_id: '' },
    asr_config: { enabled: false, model_id: '', language: '' },
    storage_provider_config: { provider: 'local' },
    storage_config: { provider: 'local' },
    wiki_config: {
      synthesis_model_id: '',
      max_pages_per_ingest: 0,
      extraction_granularity: 'standard',
    },
    indexing_strategy: {
      vector_enabled: true,
      keyword_enabled: true,
      wiki_enabled: aiEnabled,
      graph_enabled: false,
    },
    extract_config: { enabled: false },
    question_generation_config: {
      enabled: true,
      question_count: 3,
    },
  };
  if (options.description) payload.description = options.description;
  if (options.title) payload.title = options.title;
  if (options.content) payload.content = options.content;
  return payload;
};

export {
  buildGameWikiKbDetailUrl,
  type IGameWikiKbDetailUrlParams,
} from '../../utils/kbDetailUrl';

/** HTML → Markdown 转换器（模块级单例，与 DS 报告保存同源） */
let _htmlToMdConverter: HTMLToMarkdownConverter | null = null;

/** 复制等场景：原始 answer.text 中的协议标签整段删除 */
const ANSWER_PROTOCOL_TAG_REGEX =
  /<(databrain_data|databraindata|dbd|cos|tid|bid|reference|storage|btn)>([\s\S]*?)<\/\1>\s*\n?/gi;

export const stripAnswerProtocolTags = (text: string): string =>
  text.replace(ANSWER_PROTOCOL_TAG_REGEX, '');

/**
 * 将 HTML 转换为 Markdown（复用 ChatDSTemplate HTMLToMarkdownConverter）
 */
export const convertHtmlToMarkdown = async (html: string): Promise<string> => {
  if (!_htmlToMdConverter) {
    _htmlToMdConverter = new HTMLToMarkdownConverter();
  }
  return _htmlToMdConverter.convertAsync(html);
};

/** 提交成功提示：解析当前目标知识库名称 */
export const resolveKbDisplayName = (
  tab: TKnowledgeAddTab,
  values: Record<string, unknown>,
  options: IKnowledgeBaseItem[],
  kbOptionsEmpty = false,
): string => {
  if (tab === 'new' || kbOptionsEmpty) {
    return String(values.kbName ?? '').trim();
  }
  const kbId = String(values.kbId ?? '');
  return options.find((item) => item.kbId === kbId)?.kbName ?? '';
};

/** contentEditable 清空后浏览器常留 <br>/<p></p>，提取纯文本用于判空 */
export const getEditablePlainText = (html: string): string => {
  const trimmed = String(html ?? '').trim();
  if (!trimmed) return '';
  if (typeof document === 'undefined') {
    return trimmed
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .trim();
  }
  const div = document.createElement('div');
  div.innerHTML = trimmed;
  return (div.textContent || div.innerText || '')
    .replace(/\u00a0/g, ' ')
    .trim();
};

/** 正文是否为空（含仅 HTML 占位符的情况） */
export const isEditableHtmlEmpty = (html: string): boolean =>
  !getEditablePlainText(html);

/** 回写表单：空内容统一为 ''，避免残留 HTML 绕过校验 */
export const normalizeEditableFormValue = (html: string): string =>
  isEditableHtmlEmpty(html) ? '' : String(html ?? '').trim();
